import { useState } from "react";

import { Button, Classes, Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { nanoid } from "nanoid";

import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { Box, Flex } from "@/components/layout/flexbox";
import { TimeZoneSelector } from "@/components/pieces/form/TimeZoneSelector";
import { TimeZoneSelectorContainerStyled } from "@/components/pieces/schedule/SelectTimeSession/styles";
import {
  BRAND_PURPLE,
  CTA_COLOR,
  INNER_BORDER_COLOR,
  LIGHTEST_BACKGROUND,
  MAINTEXT_COLOR,
  SUBTEXT_COLOR,
} from "@/css/constants";
import { TimeSlot } from "@/types/datetime";
import { SelectFn, SelectOption } from "@/types/select";
import { formatDate } from "@/utils/datetime";

interface TimeSlotItemProps {
  slot: TimeSlot;
  selected: boolean;
  onSelect: (slot: TimeSlot) => void;
  timezone: string;
  itemCss?: ExplicitAny;
  isCoachOwner?: boolean;
  available?: boolean;
}

function TimeSlotItem({
  slot,
  selected,
  onSelect,
  timezone,
  itemCss,
  isCoachOwner = false,
  available = false,
}: TimeSlotItemProps) {
  return (
    <Box
      className="timeslot-item"
      css={{ cursor: isCoachOwner ? "default" : "pointer" }}
      onClick={isCoachOwner ? () => {} : () => onSelect(slot)}
    >
      <TimeSlotItemStyled
        className={isCoachOwner ? "disable" : undefined}
        selected={isCoachOwner ? false : selected}
        css={itemCss}
      >
        <Flex width="8" height="8" mr={1}>
          {available && (
            <svg height="8" width="8">
              <circle r="4" cx="4" cy="4" fill="#27AE60" />
            </svg>
          )}
        </Flex>
        {formatDate(slot.timeSlot, "h:mm a", timezone)}
      </TimeSlotItemStyled>
    </Box>
  );
}

function EmptyTimeSlotResult() {
  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      css={{
        backgroundColor: LIGHTEST_BACKGROUND,
        padding: "12px",
        margin: "12px 0",
        borderRadius: "8px",
      }}
    >
      <Box
        css={{ fontSize: "18px", fontWeight: "bold", color: MAINTEXT_COLOR }}
      >
        No times available
      </Box>
      <Box css={{ fontSize: "12px" }}>Please select another date</Box>
    </Flex>
  );
}

interface SelectedTimeSlotsProps {
  selectedSlots: TimeSlot[];
  timezone: string;
  togglePreferredSlots: (slot: TimeSlot) => void;
  unSelectTimeSlot: (slot: TimeSlot) => void;
  isHorizontalMode?: boolean;
}

export function SelectedTimeSlots({
  selectedSlots,
  timezone,
  togglePreferredSlots,
  unSelectTimeSlot,
  isHorizontalMode = false,
}: SelectedTimeSlotsProps) {
  const sortedSelectedSlots = selectedSlots.sort(
    (a, b) => new Date(a.timeSlot).valueOf() - new Date(b.timeSlot).valueOf(),
  );

  return (
    <Box width={"100%"}>
      <Box className="bbs" my={3} />
      <Box fontWeight={600}>Selected times</Box>
      <Box minHeight={isHorizontalMode ? 300 : 0}>
        {sortedSelectedSlots.map((item) => {
          return (
            <SelectedTimeSlotStyled
              key={`time-${item.timeSlot}`}
              alignItems="center"
              mt={2}
            >
              <Flex alignItems="center" flex={1}>
                <Flex
                  css={{ cursor: "pointer" }}
                  onClick={() => {
                    togglePreferredSlots(item);
                  }}
                >
                  <Tooltip content="Set as preferred time">
                    <Icon
                      icon="star"
                      color={item.isPreferred ? "#EAAF23" : "#E2E2E2"}
                      style={{ verticalAlign: "middle", display: "flex" }}
                    />
                  </Tooltip>
                </Flex>
                <Box
                  css={{ cursor: "pointer" }}
                  ml={2}
                  fontWeight={"normal"}
                  onClick={() => {
                    togglePreferredSlots(item);
                  }}
                >
                  {formatDate(
                    item.timeSlot,
                    "eeee, MMM do 'at' h:mm a z",
                    timezone,
                  )}
                </Box>
              </Flex>
              <Flex
                css={{ cursor: "pointer" }}
                alignItems="center"
                color={SUBTEXT_COLOR}
                onClick={() => {
                  unSelectTimeSlot(item);
                }}
              >
                <Icon icon="cross" />
              </Flex>
            </SelectedTimeSlotStyled>
          );
        })}
      </Box>
    </Box>
  );
}

export const getNewSelectedTimeSlots = (
  timeSlot: TimeSlot,
  isMultiselect: boolean | null,
  selectedSlots: TimeSlot[],
) => {
  const timeslotToAddOrRemove = {
    timeSlot: timeSlot.timeSlot,
    isPreferred: false,
  } as TimeSlot;
  // handle single case first for early return
  if (!isMultiselect) {
    return [timeslotToAddOrRemove];
  }

  const foundItemIndex = selectedSlots.findIndex(
    (item) => item.timeSlot === timeSlot.timeSlot,
  );
  return foundItemIndex > -1
    ? selectedSlots.filter((item) => item.timeSlot !== timeSlot.timeSlot)
    : ([...selectedSlots, timeslotToAddOrRemove] as TimeSlot[]);
};

interface TimeSlotSelectorProps {
  timeSlots: TimeSlot[];
  availableTimeSlots?: TimeSlot[];
  loading: boolean;
  timezone: string;
  itemCss?: ExplicitAny;
  isCoachOwner?: boolean;
  maxShowByDefault?: number | null;
  isMultiselect?: boolean;
  visibleTimeZone?: boolean;
  onChangeTimezone: SelectFn<SelectOption>;
  visibleSelectedtimes?: boolean;
  selectedSlots?: TimeSlot[];
  onChangeTimeSlots?: (slots: TimeSlot[]) => void;
}

export function TimeSlotSelector({
  timeSlots,
  availableTimeSlots = [],
  loading,
  timezone,
  itemCss,
  isCoachOwner = false,
  maxShowByDefault,
  isMultiselect = false,
  visibleTimeZone = false,
  onChangeTimezone,
  visibleSelectedtimes = false,
  selectedSlots = [],
  onChangeTimeSlots,
}: TimeSlotSelectorProps) {
  const [showingAll, setShowingAll] = useState(!maxShowByDefault);
  const toggleSelectTimeSlot = (timeSlot: TimeSlot) => {
    const newTimeslots = getNewSelectedTimeSlots(
      timeSlot,
      isMultiselect,
      selectedSlots,
    );
    onChangeTimeSlots?.(newTimeslots);
  };

  const togglePreferredSlots = (timeSlot: TimeSlot) => {
    const newTimeslots = selectedSlots.map((item) => {
      return item.timeSlot === timeSlot.timeSlot
        ? { ...item, isPreferred: !item.isPreferred }
        : item;
    });
    onChangeTimeSlots?.(newTimeslots);
  };

  if (loading) {
    return (
      <Box
        position="relative"
        css={{
          padding: "12px 0",
        }}
      >
        {[...Array(3)].map((_, index) => (
          <Box
            key={index}
            style={{ height: "52px", borderRadius: "8px", margin: "12px 0" }}
            className={Classes.SKELETON}
          ></Box>
        ))}
      </Box>
    );
  }

  if (!timeSlots) {
    return null;
  }

  const visibleSelectedTime =
    visibleSelectedtimes && isMultiselect && selectedSlots.length > 0;

  const selectedTimes = visibleSelectedTime && (
    <SelectedTimeSlots
      selectedSlots={selectedSlots}
      timezone={timezone}
      unSelectTimeSlot={toggleSelectTimeSlot}
      togglePreferredSlots={togglePreferredSlots}
    />
  );

  if (timeSlots.length === 0) {
    return (
      <>
        <EmptyTimeSlotResult />
        {selectedTimes}
      </>
    );
  }

  let displayedTimeSlots = timeSlots.map((item) => {
    return { timeSlot: item.timeSlot, isPreferred: false };
  });

  const slotsTruncated =
    maxShowByDefault &&
    displayedTimeSlots.length > maxShowByDefault &&
    !showingAll;

  if (slotsTruncated) {
    displayedTimeSlots = displayedTimeSlots.slice(0, maxShowByDefault);
  }

  return (
    <Box
      css={{
        padding: "12px 0",
      }}
      position="relative"
      className="timeslots-selector"
    >
      {displayedTimeSlots.map((t) => {
        const selected =
          selectedSlots.findIndex((item) => item.timeSlot === t.timeSlot) > -1;
        const available =
          availableTimeSlots.findIndex((item) => item.timeSlot === t.timeSlot) >
          -1;
        return (
          <TimeSlotItem
            key={nanoid()}
            slot={t}
            selected={selected}
            available={available}
            timezone={timezone}
            onSelect={toggleSelectTimeSlot}
            itemCss={itemCss}
            isCoachOwner={isCoachOwner}
          />
        );
      })}
      {slotsTruncated && (
        <Box mb={24} width="100%">
          <Button fill={true} onClick={() => setShowingAll(true)}>
            <Box color={CTA_COLOR} fontWeight={600}>
              See More
            </Box>
          </Button>
        </Box>
      )}

      {visibleTimeZone && (
        <TimeZoneSelectorContainerStyled width={"100%"}>
          <TimeZoneSelector
            value={timezone}
            onChange={onChangeTimezone}
            className="select-time-range select-time-range-mini"
          />
        </TimeZoneSelectorContainerStyled>
      )}

      {selectedTimes}
    </Box>
  );
}

export const TimeSlotItemStyled = styled(Flex)<{
  selected?: boolean;
  css?: ExplicitAny;
}>`
  border-radius: 8px;
  text-wrap: nowrap;
  border: ${(props) =>
    props.selected
      ? `2px solid ${BRAND_PURPLE}`
      : `2px solid ${INNER_BORDER_COLOR}`};
  color: ${(props) => (props.selected ? BRAND_PURPLE : MAINTEXT_COLOR)};
  background-color: ${(props) =>
    props.selected ? "rgba(100, 102, 235, 0.05)" : "transparent"};
  padding: 12px;

  margin-bottom: 12px;
  font-size: 18px;
  font-weight: 600;

  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  -webkit-transition: all 0.3s ease-out;
  -moz-transition: all 0.3s ease-out;
  -o-transition: all 0.3s ease-out;
  transition: all 0.3s ease-out;

  &:hover {
    border: 2px solid ${BRAND_PURPLE};
    color: ${BRAND_PURPLE};
    background-color: rgba(100, 102, 235, 0.05);
  }

  &.disable {
    border: 2px solid #f6f6fb;
    color: #000;
    background-color: #f6f6fb;
    cursor: default;
  }
`;

const SelectedTimeSlotStyled = styled(Flex)`
  &:hover {
    color: ${BRAND_PURPLE};
  }
`;
